// Public post-service NPS response page (no login). The survey email links here. The
// customer sees one question, picks a score from 0 to 10 (colour-coded), optionally adds a
// comment, and submits. Mobile-first, branded. House rules: plain English, no personal
// names, the single client phone number.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PublicNpsView } from "../../lib/api";
import { FullScreenLoader } from "../../components/Loader";
import { PrivacyNoticeLink } from "../../components/PrivacyNoticeLink";

const PHONE = "0203 576 4760";

// Colour band for a score button: 0 to 6 red, 7 to 8 amber, 9 to 10 green. The red/amber
// tints use standard Tailwind palette colours (alpha modifiers work); the green tint uses an
// arbitrary brand hex (the ele-* CSS-variable tokens do NOT support Tailwind alpha modifiers).
function scoreTone(score: number, selected: boolean): string {
  if (selected) {
    if (score <= 6) return "border-red-500 bg-red-500/20 text-red-300";
    if (score <= 8) return "border-amber-400 bg-amber-400/20 text-amber-300";
    return "border-ele-green bg-[#63C41F]/20 text-ele-green";
  }
  return "border-ele-purple-accent text-ele-body hover:border-ele-green";
}

export function NpsResponsePage() {
  const { token = "" } = useParams();
  const [view, setView] = useState<PublicNpsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<"not_found" | "error" | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.publicNps
      .get(token)
      .then((v) => {
        if (!live) return;
        setView(v);
        if (v.status === "responded") setDone(true);
      })
      .catch((err) => {
        if (!live) return;
        setLoadError(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  async function submit() {
    if (score == null || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.publicNps.submit(token, score, comment.trim() || undefined);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setSubmitError(`Please wait a moment and try again, or call the office on ${PHONE}.`);
      } else {
        setSubmitError(`Something went wrong. Please try again, or call the office on ${PHONE}.`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <FullScreenLoader />;

  if (loadError) {
    return (
      <Shell>
        <div className="rounded-ele border border-ele-purple-accent bg-black/20 p-6 text-center">
          <h2 className="mb-2 font-heading text-2xl text-ele-white">
            {loadError === "not_found" ? "We could not find this survey" : "Something went wrong"}
          </h2>
          <p className="text-ele-body">
            {loadError === "not_found"
              ? "This link may have expired or already been used. Thank you all the same."
              : "Please try again in a moment. If the trouble continues, we would still like to hear from you."}
          </p>
          <p className="mt-4 font-menu text-sm text-ele-blue">Call us on {PHONE}</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="rounded-ele border border-ele-green bg-black/20 p-6 text-center">
          <div aria-hidden="true" className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-ele-green text-2xl font-bold text-ele-bg">
            {"✓"}
          </div>
          <h2 className="mb-2 font-heading text-2xl text-ele-white">Thank you</h2>
          <p className="mx-auto max-w-md text-ele-body">
            We appreciate your feedback. It helps us keep improving the service. If you need us, please call the office on {PHONE}.
          </p>
        </div>
      </Shell>
    );
  }

  const site = view?.siteName?.trim();
  return (
    <Shell>
      <header className="mb-6">
        <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">Your feedback</p>
        <h1 className="font-heading text-2xl tracking-wide text-ele-white sm:text-3xl">How did we do?</h1>
        {site ? <p className="mt-1 text-sm text-ele-body">Your recent service at {site}.</p> : null}
      </header>

      <p className="mb-3 text-ele-body">
        How likely are you to recommend East London Elevators to a colleague or another building manager? Please select a score.
      </p>
      <div className="grid grid-cols-6 gap-2 sm:grid-cols-11">
        {Array.from({ length: 11 }, (_, n) => (
          <button
            key={n}
            type="button"
            aria-pressed={score === n}
            onClick={() => setScore(n)}
            className={`rounded-ele border py-3 font-heading text-lg transition ${scoreTone(n, score === n)}`}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-xs text-ele-body opacity-60">
        <span>Not at all likely</span>
        <span>Extremely likely</span>
      </div>

      <label className="mt-5 block">
        <span className="mb-1 block text-sm text-ele-body">Anything we could do better? (optional)</span>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={2000}
          className="w-full rounded-ele border border-ele-purple-accent bg-ele-bg px-3 py-2 text-ele-white outline-none focus:border-ele-green"
        />
      </label>

      {submitError && (
        <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-black/20 px-3 py-2 text-sm text-ele-white">
          {submitError}
        </p>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={submitting || score == null}
        className="mt-5 w-full rounded-ele bg-ele-green py-3 font-heading text-lg uppercase tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50 sm:w-auto sm:px-10"
      >
        {submitting ? "Sending..." : "Submit feedback"}
      </button>

      <Footer />
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh bg-ele-bg px-4 py-8 text-ele-body">
      <div className="mx-auto w-full max-w-2xl">
        <div className="mb-6">
          <img src="/brand/ele-logo.png" alt="East London Elevators" className="h-9 w-auto" />
        </div>
        {children}
      </div>
    </div>
  );
}

function Footer() {
  // Session ASQ (Stream G, Item 5): the dimming moved from the footer onto its lines, so the privacy notice link (shown
  // only once the notice is published) keeps its full contrast on this dark canvas. The existing lines read as before.
  return (
    <footer className="mt-10 border-t border-ele-purple-accent pt-4 text-xs text-ele-body">
      <div className="opacity-60">
        <p>For and on behalf of East London Elevators.</p>
        <p className="mt-1">East London Elevators Ltd, company 15429089, VAT GB493339461 | {PHONE} | info@eastlondonelevators.co.uk</p>
      </div>
      <PrivacyNoticeLink className="mt-2" />
    </footer>
  );
}
